import React, { useState } from 'react';
import { 
  ArrowLeft,
  Plus,
  Upload,
  MoreVertical,
  Grid,
  List,
  Download,
  Trash2,
  CheckSquare,
  Square,
  Bell,
  TrendingUp,
  FileText,
  AlertTriangle,
  X
} from 'lucide-react';

const MobileDocumentHeader = ({
  onBack,
  onUpload,
  onQuickUpload,
  selectedCount,
  totalCount,
  onSelectAll,
  onBulkAction,
  onClearSelection,
  viewMode,
  onViewModeChange,
  documentInsights
}) => {
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  return (
    <>
      {/* Main Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        {selectedCount > 0 ? (
          /* Selection Mode */
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={onClearSelection}
                className="p-1 text-gray-600 hover:text-gray-800"
              >
                <X className="h-6 w-6" />
              </button>
              <span className="text-lg font-medium text-gray-900">
                {selectedCount} selected
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onBulkAction('download')}
                className="p-2 text-green-600 hover:text-green-800 bg-green-50 rounded-lg"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={() => onBulkAction('delete')}
                className="p-2 text-red-600 hover:text-red-800 bg-red-50 rounded-lg"
              >
                <Trash2 className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="p-2 text-gray-600 hover:text-gray-800 bg-gray-50 rounded-lg"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          /* Normal Mode */
          <div className="px-4 py-3">
            {/* Top Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onBack}
                  className="p-1 text-gray-600 hover:text-gray-800"
                >
                  <ArrowLeft className="h-6 w-6" />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Documents</h1>
                  <p className="text-sm text-gray-500">{totalCount} documents</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowInsights(!showInsights)}
                  className="p-2 text-gray-600 hover:text-gray-800 bg-gray-50 rounded-lg relative"
                >
                  <TrendingUp className="h-5 w-5" />
                  {documentInsights.needsAttention > 0 && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
                  )}
                </button>
                
                <div className="border border-gray-300 rounded-lg p-1 bg-gray-50">
                  <button
                    onClick={() => onViewModeChange('list')}
                    className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onViewModeChange('grid')}
                    className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={onQuickUpload}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Upload className="h-4 w-4 mr-2" />
                Quick Upload
              </button>
              <button
                onClick={onUpload}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Document
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions Menu */}
        {showBulkActions && selectedCount > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  onSelectAll();
                  setShowBulkActions(false);
                }}
                className="flex items-center justify-center space-x-2 p-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <CheckSquare className="h-4 w-4" />
                <span>Select All</span>
              </button>
              <button
                onClick={() => {
                  // Add archive functionality
                  setShowBulkActions(false);
                }}
                className="flex items-center justify-center space-x-2 p-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FileText className="h-4 w-4" />
                <span>Archive</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Insights Panel */}
      {showInsights && (
        <div className="bg-white border-b border-gray-200 shadow-lg">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Document Health</h3>
              <button
                onClick={() => setShowInsights(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  documentInsights.healthScore >= 80 ? 'text-green-600' : 
                  documentInsights.healthScore >= 60 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {documentInsights.healthScore}%
                </div>
                <div className="text-xs text-gray-500">Health Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {documentInsights.needsAttention}
                </div>
                <div className="text-xs text-gray-500">Need Attention</div>
              </div>
            </div>

            {/* Urgent Items */}
            {documentInsights.upcomingExpirations.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mr-2" />
                  <span className="text-sm font-medium text-amber-800">
                    Expiring Soon
                  </span>
                </div>
                <div className="space-y-1">
                  {documentInsights.upcomingExpirations.slice(0, 2).map((doc) => (
                    <div key={doc.id} className="text-xs text-amber-700">
                      {doc.title} - {doc.daysLeft} days left
                    </div>
                  ))}
                  {documentInsights.upcomingExpirations.length > 2 && (
                    <div className="text-xs text-amber-600 font-medium">
                      +{documentInsights.upcomingExpirations.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileDocumentHeader;